import React, { createContext, useState } from "react";
import { transformUserPermissionArr } from "../components/common/CommonFunctions";

export const AuthContext = createContext("");

export const SessionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Define all keys used in storage
  const storageKeys = [
    "token",
    "is_super_admin",
    "is_admin",
    "userId",
    "user_group_id",
    "address",
    "companyName",
    "profile",
    "employee_id",
    "employee_name",
    "designation",
    "department",
    "loginUserPermission",
    "modules",
    "subModule",
    "planName",
    "is_password_updated",
    "passwordPolicy",
    "shiftDetails",
    "customConfig",
    "salary_calculation",
  ];

  /** --------------------------
   *  Helpers
   *  ------------------------- */
  const setStorage = (storage, data) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        storage.setItem(key, typeof value === "object" ? JSON.stringify(value) : value);
      }
    });
  };

  const removeStorage = (storage) => {
    storageKeys.forEach((key) => storage.removeItem(key));
  };

  const parsePermissions = (loginData) => {
    return loginData?.permissions?.permissions?.length
      ? transformUserPermissionArr([...loginData.permissions.permissions])
      : [];
  };

  /** --------------------------
   *  Login
   *  ------------------------- */
  const loginauth = async (loginData, keepSignedIn) => {
    try {
      const dataList = parsePermissions(loginData);
      const moduleList = loginData?.modules ? [...loginData.modules] : [];
      const subModuleList = loginData?.sub_modules ? [...loginData.sub_modules] : [];

      const commonData = {
        token: loginData.token,
        is_super_admin: loginData.is_super_admin,
        is_admin: loginData.is_admin,
        userId: loginData.user_id,
        user_group_id: loginData.user_group_id,
        address: loginData.address,
        companyName: loginData.company_name,
        profile: loginData.profile,
        employee_id: loginData.employee_id,
        employee_name: loginData.employee_name,
        designation: loginData.designation,
        department: loginData.department,
        loginUserPermission: dataList,
        modules: moduleList,
        subModule: subModuleList,
        planName: loginData.plan_name,
        is_password_updated: loginData.is_password_updated,
        reportingManager: loginData.is_reproting_manager,
      };

      // Always save in sessionStorage
      setStorage(sessionStorage, commonData);

      // Optionally save in localStorage
      if (keepSignedIn) {
        const now = new Date();
        setStorage(localStorage, {
          ...commonData,
          keepSignedIn: true,
          keepSignedInExpiry: now.getTime() + 720 * 60 * 1000, // 12 hours
        });
      }

      setIsAuthenticated(true);
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  /** --------------------------
   *  Logout
   *  ------------------------- */
  const logoutauth = () => {
    removeStorage(sessionStorage);
    removeStorage(localStorage);
    localStorage.removeItem("keepSignedIn");
    localStorage.removeItem("keepSignedInExpiry");
    localStorage.removeItem('sidebar_preference');
    setIsAuthenticated(false);
  };

  /** --------------------------
   *  Sync sessionStorage with localStorage
   *  ------------------------- */
  const setSessionStorage = () => {
    const syncedData = {};
    storageKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) syncedData[key] = value;
    });
    setStorage(sessionStorage, syncedData);
  };

  /** --------------------------
   *  Expiry checker
   *  ------------------------- */
  const getWithExpiry = () => {
    const expiry = localStorage.getItem("keepSignedInExpiry");
    if (!expiry) return false;
    return new Date().getTime() <= Number(expiry);
  };

  /** --------------------------
   *  Check Authentication
   *  ------------------------- */
  const checkAuth = () => {
    if (localStorage.getItem("keepSignedIn")) {
      if (getWithExpiry()) {
        setSessionStorage();
        if (sessionStorage.getItem("token")) setIsAuthenticated(true);
      } else {
        logoutauth();
      }
    } else {
      if (sessionStorage.getItem("token")) setIsAuthenticated(true);
    }
  };

  /** --------------------------
   *  Helpers exposed
   *  ------------------------- */
  const loginServiceType = () => sessionStorage.getItem("is_admin");

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loginauth,
        logoutauth,
        checkAuth,
        loginServiceType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
